import React from "react";
import { CheckCircleIcon, XCircleIcon } from "@iris/react-icons";
import {
  ComparisonValue,
  BuyerLandingWhyChooseAutotraderProps,
  comparisonColumns,
  comparisonRows,
  COMPARISON_VALUE_TYPES,
} from "@/constants/comparisonData";
import ChangeStateFooter from "./ChangeStateFooter";
import { Heading, Body, Box, Flex, Divider, Section } from "@iris/react-components";

const toTestIdToken = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function renderComparisonValue(value: ComparisonValue, colIndex: number) {
  const textColor =
    colIndex === 0 ? "iris:text-(--base-color-neutral-800)" : "iris:text-(--base-color-neutral-600)";
  return value.type === COMPARISON_VALUE_TYPES.CHECK ? (
    <>
      <Box className="iris:h-[24px]">
        <CheckCircleIcon
          width={16}
          height={16}
          className="iris:relative iris:top-[1px] iris:text-(--base-color-success-700)"
        />
      </Box>
      <Body
        as="div"
        className={`iris:text-[16px] iris:max-[1024px]:text-[14px] ${textColor}`}
      >
        {value.text}
      </Body>
    </>
  ) : value.type === COMPARISON_VALUE_TYPES.CROSS ? (
    <Box className="iris:h-[24px]">
        <XCircleIcon width={24} height={24} className="iris:text-(--base-color-error-700)" />
    </Box>
  ) : (
    <Body
        as="div"
        className={`iris:text-[16px] iris:max-[1024px]:text-[14px] ${textColor}`}
      >
        {value.text}
    </Body>
  );
}

// NOTE: iris:! overrides are intentional in this component.
// 1. @atc/axl-atc theme styles override Iris defaults due to higher specificity.
// 2. Iris defaults are intentionally overridden here to match the approved design.
export default function WhyChooseAutotraderSection({
  eligibility = false,
  locationData,
  onZipChange,
}: BuyerLandingWhyChooseAutotraderProps) {
  const displayedComparisonRows = eligibility
    ? comparisonRows
    : comparisonRows.filter(
        (row) => row.feature !== "Registration & Title Fees, Taxes",
      );

  // iris-override (token unavailable): Section lacks flex display prop; using iris:!flex for column layout
  return (
    <Section
      className="why-choose-autotrader iris:!m-0 iris:px-[16px] iris:min-[1025px]:px-[40px] iris:py-[40px] iris:bg-white iris:!flex iris:flex-col iris:items-center iris:gap-[32px]"
      aria-labelledby="why-choose-title"
      maxWidth="none"
      data-testid="why-choose-section"
    >
      <Flex
        align="center"
        as="div"
        className="iris:gap-[16px]"
        direction="col"
        justify="center"
      >
        {/* iris-override (token unavailable): Heading lacks font-weight/font-size/color props for this design variant; using iris:! overrides */}
        <Heading
          id="why-choose-title"
          data-testid="why-choose-title"
          as="h2"
          className="iris:!font-[900] iris:!text-[40px] iris:leading-[120%] iris:!text-(--base-color-primary-800) iris:m-0"
        >
          Why Choose Autotrader?
        </Heading>
        {/* iris-override (token unavailable): Body lacks margin=0 prop; using iris:!m-0 to remove default paragraph margin */}
        <Body
          as="p"
          data-testid="why-choose-subtitle"
          className="iris:!m-0 iris:text-[16px] iris:min-[1025px]:text-[18px]"
        >
          See how Autotrader Private Seller Exchange stacks up against the
          competition.
        </Body>
      </Flex>
      <Box
        className="iris:w-[1000px] iris:max-[1024px]:w-full iris:mx-auto"
        role="region"
        aria-label="Autotrader comparison details"
      >
        <Flex
          as="div"
          direction="row"
          className="iris:gap-[24px] iris:max-[1024px]:gap-0"
          data-testid="compare-header"
        >
          <Flex as="div" direction="col" className="iris:w-[226px] iris:max-[1024px]:hidden" aria-hidden="true" />
          {comparisonColumns.map((column, index) => (
            <Flex
              as="div"
              direction="col"
              className={`iris:flex-1 ${index === 0 ? "first-col" : ""}`}
              key={`header-${column.title}`}
              data-testid={`compare-header-item-${toTestIdToken(column.title)}`}
            >
              <Box className={`iris:p-[8px] iris:pb-[10px] ${index === 0 ? "iris:pl-0" : ""}`}>
                {/* iris-override (token unavailable): Body lacks font-weight prop for bold variant; using iris:!font-bold for comparison column header */}
                <Body
                  as="div"
                  className={`iris:text-[16px] iris:min-[1025px]:text-[18px] iris:!font-bold ${
                    index === 0 ? "iris:text-(--base-color-secondary-700)" : ""
                  }`}
                >
                  {column.title}
                </Body>
                <Body
                  as="div"
                  className="iris:text-[12px] iris:min-[1025px]:text-[16px]"
                >
                  {column.subtitle}
                </Body>
              </Box>
            </Flex>
          ))}
        </Flex>
        <Divider
          aria-hidden="true"
          className="iris:my-[8px] iris:max-[1024px]:mt-0 iris:max-[1024px]:mb-[2px] iris:bg-(--base-color-black)/20"
        />
        <Flex
          as="div"
          direction="col"
          className="iris:gap-[8px] iris:max-[1024px]:gap-0"
        >
          {displayedComparisonRows.map((row, rowIndex) => (
            <React.Fragment key={`row-fragment-${row.feature}`}>
              <Flex
                as="div"
                direction="row"
                className="iris:gap-[24px] iris:py-[10px] iris:max-[1024px]:flex-col iris:max-[1024px]:gap-[2px] iris:max-[1024px]:p-0"
                data-testid={`compare-row-${toTestIdToken(row.feature)}`}
              >
                {/* iris-override (token unavailable): Body lacks font-weight prop; using iris:!font-[700] for comparison row feature label */}
                <Body
                  as="div"
                  className="iris:w-[226px] iris:max-[1024px]:pt-[10px] iris:!font-[700] iris:max-[1024px]:text-[14px]"
                >
                  {row.feature}
                </Body>
                <Flex
                  as="div"
                  direction="row"
                  className="iris:flex-1 iris:gap-[24px] iris:max-[1024px]:gap-0 iris:max-[1024px]:py-[8px] iris:max-[1024px]:px-0"
                >
                  {row.values.map((value, index) => (
                    <Box
                      className={`iris:flex-1 ${index === 0 ? "first-col" : ""}`}
                      key={`${row.feature}-${comparisonColumns[index].title}`}
                      data-testid={`compare-item-${toTestIdToken(row.feature)}-${toTestIdToken(comparisonColumns[index].title)}`}
                    >
                      <Flex
                        as="div"
                        direction="row"
                        className={`iris:gap-[7px] iris:max-[1024px]:gap-[4px] iris:px-[8px] ${
                          index === 0 ? "iris:pl-0" : ""
                        }`}
                      >
                        {renderComparisonValue(value, index)}
                      </Flex>
                    </Box>
                  ))}
                </Flex>
              </Flex>
              {rowIndex !== displayedComparisonRows.length - 1 && (
                <Divider
                  aria-hidden="true"
                  className="iris:hidden iris:max-[1024px]:block iris:my-[2px] iris:bg-(--base-color-black)/20"
                />
              )}
            </React.Fragment>
          ))}
        </Flex>
      </Box>
      <Box className="footer" data-testid="why-choose-footer">
        <ChangeStateFooter
          text="*This service is provided if you register the vehicle in"
          selectedState={locationData?.stateName || ""}
          currentZip={locationData?.zip || ""}
          onSave={onZipChange ?? (() => Promise.resolve())}
          modalText="Autotrader Private Seller Exchange services differ based on what state you are registering the car. Enter the ZIP code where you are registering your vehicle."
        />
      </Box>
    </Section>
  );
}
